import React, { useState, useContext } from 'react';
import { 
  Button, 
  TextField, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogContentText, 
  DialogTitle,
  CircularProgress,
  Snackbar,
  Paper,
  Typography
} from '@material-ui/core';
import { AuthContext } from '../Context/AuthContext';
import axios from 'axios';
import moment from 'moment';
import config from '../config';

// Custom Alert component as a fallback in case @material-ui/lab is not available
function CustomAlert({ severity, onClose, children }) {
  const getBackgroundColor = () => {
    switch (severity) {
      case 'error':
        return '#f44336';
      case 'warning':
        return '#ff9800';
      case 'info':
        return '#2196f3';
      case 'success':
        return '#4caf50';
      default:
        return '#2196f3';
    }
  };

  return (
    <Paper 
      style={{ 
        backgroundColor: getBackgroundColor(),
        color: 'white',
        padding: '10px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}
    >
      <Typography>{children}</Typography>
      {onClose && (
        <Button color="inherit" size="small" onClick={onClose}>
          CLOSE
        </Button>
      )}
    </Paper>
  );
}

// Try to import MuiAlert, but use CustomAlert as fallback
let Alert = CustomAlert;
try {
  // Dynamic import to avoid build errors if the package is not available
  const MuiAlert = require('@material-ui/lab/Alert').default;
  Alert = (props) => <MuiAlert elevation={6} variant="filled" {...props} />;
} catch (error) {
  console.warn('Using CustomAlert as @material-ui/lab/Alert is not available');
}

function ReservationForm({ bookId, bookName, open, handleClose, onReservationComplete }) {
  const { user } = useContext(AuthContext);
  const API_URL = config.API_URL;
  
  const [startDate, setStartDate] = useState(moment().format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(moment().add(7, 'days').format('YYYY-MM-DD'));
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validate dates
      if (moment(startDate).isBefore(moment(), 'day')) {
        setError('Start date cannot be in the past');
        setLoading(false);
        return;
      }

      if (moment(endDate).isBefore(moment(startDate), 'day')) {
        setError('End date cannot be before start date');
        setLoading(false);
        return;
      }

      if (moment(endDate).diff(moment(startDate), 'days') > 14) {
        setError('Reservation period cannot exceed 14 days');
        setLoading(false);
        return;
      }

      // Create reservation
      const response = await axios.post(`${API_URL}api/reservations`, {
        userId: user._id,
        bookId: bookId,
        startDate: startDate,
        endDate: endDate,
        notes: notes
      });

      setSuccess(true);
      setLoading(false);
      
      // Notify parent component that reservation is complete
      if (onReservationComplete) {
        onReservationComplete(response.data);
      }

      // Close dialog after 2 seconds
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err) {
      setLoading(false);
      if (err.response && err.response.data) {
        setError(err.response.data);
      } else {
        setError('An error occurred while making the reservation');
      }
      console.error('Error making reservation:', err);
    }
  };

  const handleCloseSnackbar = () => {
    setError('');
    setSuccess(false);
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} aria-labelledby="form-dialog-title">
        <DialogTitle id="form-dialog-title">Reserve Book: {bookName}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please select the dates for your book reservation. The maximum reservation period is 14 days.
          </DialogContentText>
          <form onSubmit={handleSubmit}>
            <TextField
              margin="dense"
              id="startDate"
              label="Start Date"
              type="date"
              fullWidth
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              required
            />
            <TextField
              margin="dense"
              id="endDate"
              label="End Date"
              type="date"
              fullWidth
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              required
            />
            <TextField
              margin="dense"
              id="notes"
              label="Notes (Optional)"
              type="text"
              fullWidth
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              multiline
              rows={2}
            />
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary" disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} color="primary" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Reserve'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!error} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity="error">
          {error}
        </Alert>
      </Snackbar>

      <Snackbar open={success} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity="success">
          Book reserved successfully!
        </Alert>
      </Snackbar>
    </>
  );
}

export default ReservationForm; 